import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Store, X, Loader2, Plus, Camera, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../services/api';

export function MenuScannerModal({
  isOpen,
  onClose,
  onAddFood,
  mealTimes,
  selectedMealTime,
  onMealTimeChange,
  savedMenus = [],
}) {
  const [restaurantName, setRestaurantName] = useState('');
  const [restaurantStreet, setRestaurantStreet] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [skipCache, setSkipCache] = useState(false);

  // Compress image before sending
  const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        const base64 = canvas.toDataURL('image/jpeg', quality);
        resolve(base64);
      };
      
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !restaurantName || !restaurantStreet) {
      if (!restaurantName || !restaurantStreet) {
        toast.error('Please enter restaurant name and street first');
      }
      return;
    }
    
    setIsScanning(true);
    
    try {
      const base64 = await compressImage(file);
      console.log('Menu image compressed, length:', base64.length);
      
      const result = await api.post('/nutrition/scan-menu', {
        image_data: base64,
        restaurant_name: restaurantName,
        restaurant_street: restaurantStreet,
        skip_cache: skipCache
      });
      
      setScanResult(result);
      setSkipCache(false);
      
      if (result.cached) {
        toast.success(`Using cached menu (${result.cache_age_days} days old)`);
      } else {
        toast.success(`Found ${result.items?.length || 0} menu items`);
      }
    } catch (err) {
      console.error('Menu scan error:', err);
      toast.error('Failed to scan menu: ' + (err.message || 'Unknown error'));
    } finally {
      setIsScanning(false);
    }
  };

  const handleAddItem = (item, index) => {
    onAddFood({
      id: Date.now() + index,
      name: `${item.name} (${scanResult.restaurant?.name})`,
      calories: item.calories,
      protein: item.protein,
      carbs: item.carbs,
      fat: item.fat,
      servingSize: item.serving_size || '1 serving',
      source: 'restaurant_menu',
      restaurant: scanResult.restaurant?.name
    });
    toast.success(`Added ${item.name}`);
  };

  const handleLoadSavedMenu = async (menu) => {
    try {
      const details = await api.get(`/nutrition/restaurant-menu/${menu.menu_id}`);
      setScanResult(details);
      setRestaurantName(menu.restaurant_name);
      setRestaurantStreet(menu.restaurant_street);
    } catch (err) {
      toast.error('Failed to load menu');
    }
  };

  const handleScanNextPage = () => {
    setSkipCache(true);
    setScanResult(null);
  };

  const handleScanDifferentRestaurant = () => {
    setSkipCache(false);
    setScanResult(null);
    setRestaurantName('');
    setRestaurantStreet('');
  };

  const handleClose = () => {
    setScanResult(null);
    setRestaurantName('');
    setRestaurantStreet('');
    setSkipCache(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <Card className="bg-slate-800 border-slate-700 w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between shrink-0">
          <CardTitle className="text-white flex items-center gap-2">
            <Store className="h-5 w-5 text-rose-400" />
            Restaurant Menu Scanner
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="text-slate-400 hover:text-white"
          >
            <X className="h-5 w-5" />
          </Button>
        </CardHeader>
        
        <CardContent className="overflow-y-auto flex-1 space-y-4">
          {!scanResult ? (
            <>
              {/* Restaurant Info */}
              <div className="space-y-3">
                <div>
                  <Label className="text-white">Restaurant Name</Label>
                  <Input
                    value={restaurantName}
                    onChange={(e) => setRestaurantName(e.target.value)}
                    placeholder="e.g., Cafe Milano"
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white">Street Address</Label>
                  <Input
                    value={restaurantStreet}
                    onChange={(e) => setRestaurantStreet(e.target.value)}
                    placeholder="e.g., Main Street 123"
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
                <p className="text-xs text-slate-400 flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  City & Country from your profile will be used
                </p>
              </div>

              {/* Camera/Upload */}
              <div className="border-2 border-dashed border-slate-600 rounded-xl p-6 text-center">
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  id="menu-camera-input"
                  onChange={handleFileSelect}
                />
                <label
                  htmlFor="menu-camera-input"
                  className={`cursor-pointer ${(!restaurantName || !restaurantStreet) ? 'opacity-50' : ''}`}
                >
                  {isScanning ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="h-12 w-12 text-rose-400 animate-spin mb-2" />
                      <p className="text-white font-medium">Analyzing menu...</p>
                      <p className="text-slate-400 text-sm">AI is reading the menu</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Camera className="h-12 w-12 text-rose-400 mb-2" />
                      <p className="text-white font-medium">Take Photo of Menu</p>
                      <p className="text-slate-400 text-sm">or tap to upload image</p>
                    </div>
                  )}
                </label>
              </div>

              {/* Saved Menus */}
              {savedMenus.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-slate-400 font-medium">Recent Menus:</p>
                  {savedMenus.slice(0, 5).map(menu => (
                    <button
                      key={menu.menu_id}
                      onClick={() => handleLoadSavedMenu(menu)}
                      className="w-full p-3 bg-slate-700/50 rounded-lg text-left hover:bg-slate-700 transition-colors"
                    >
                      <p className="text-white font-medium">{menu.restaurant_name}</p>
                      <p className="text-xs text-slate-400">{menu.restaurant_street}</p>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            /* Menu Results */
            <div className="space-y-4">
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <p className="text-white font-medium">{scanResult.restaurant?.name}</p>
                <p className="text-xs text-slate-400">{scanResult.restaurant?.street}, {scanResult.restaurant?.city}</p>
                {scanResult.cached && (
                  <span className="text-xs text-green-400">✓ Cached ({scanResult.cache_age_days} days old)</span>
                )}
              </div>

              {/* Meal Time Selector */}
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Add to meal:</Label>
                <div className="flex flex-wrap gap-2">
                  {mealTimes.map((mealTime) => (
                    <button
                      key={mealTime.id}
                      onClick={() => onMealTimeChange(mealTime.id)}
                      className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 transition-colors ${
                        selectedMealTime === mealTime.id
                          ? 'bg-rose-500 text-white'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      <span>{mealTime.icon}</span>
                      <span>{mealTime.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <p className="text-sm text-slate-400">Select items to add:</p>
              
              <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                {scanResult.items?.map((item, index) => (
                  <div
                    key={index}
                    className="p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-white font-medium">{item.name}</p>
                        {item.description && (
                          <p className="text-xs text-slate-400 mt-0.5">{item.description}</p>
                        )}
                        <div className="flex gap-3 mt-1 text-xs">
                          <span className="text-orange-400">{item.calories} cal</span>
                          <span className="text-red-400">P: {item.protein}g</span>
                          <span className="text-yellow-400">C: {item.carbs}g</span>
                          <span className="text-blue-400">F: {item.fat}g</span>
                        </div>
                        {item.serving_size && (
                          <p className="text-xs text-slate-500 mt-0.5">{item.serving_size}</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleAddItem(item, index)}
                        className="bg-rose-500 hover:bg-rose-400 text-white"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-2">
                <Button
                  onClick={handleScanNextPage}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Scan Next Page (Same Menu)
                </Button>
                <Button
                  onClick={handleScanDifferentRestaurant}
                  variant="outline"
                  className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  Scan Different Restaurant
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default MenuScannerModal;
